/**
 * Per-service time selection (gaps allowed on a chosen date).
 * Excludes real deals/offers; duration-overflow splits (isDurationSplit) are allowed.
 */

export const SCHEDULING_MODE_CONTINUOUS = 'continuous'
export const SCHEDULING_MODE_PER_SERVICE = 'per_service'

/** Real package / offer deals — do not offer service-wise times. */
export function isOfferOrPackageDeal(deal) {
  if (!deal) return false
  if (deal.isDurationSplit) return false
  if (deal.isPackageOffer) return true
  if (deal.id) return true
  if (Number(deal.total_days) > 1 && !deal.isDurationSplit) return true
  return false
}

export function canUsePerServiceScheduling(deal, services = []) {
  if (isOfferOrPackageDeal(deal)) return false
  return (Array.isArray(services) ? services : []).length >= 2
}

export function serviceScheduleKey(service, index = 0) {
  const id = service?.id ?? service?.name ?? 'svc'
  const day = Number(service?.day_number) || 1
  return `${id}|d${day}|i${index}`
}

export function parseDurationMinutes(service) {
  if (!service) return 0
  if (typeof service.duration_minutes === 'number' && service.duration_minutes > 0) {
    return Math.floor(service.duration_minutes)
  }
  const d = service.duration
  if (typeof d === 'number' && d > 0) return Math.floor(d)
  if (typeof d === 'string') {
    let minutes = 0
    const hr = d.match(/(\d+(?:[.,]\d+)?)\s*hr/i)
    const min = d.match(/(\d+)\s*min/i)
    if (hr) minutes += Math.round(parseFloat(hr[1].replace(',', '.')) * 60)
    if (min) minutes += parseInt(min[1], 10)
    if (!hr && !min) {
      const num = d.match(/(\d+)/)
      if (num) minutes += parseInt(num[1], 10)
    }
    return minutes
  }
  return 0
}

/** Normalize UI time object / string → { startHHMM, endHHMM, slot } */
export function normalizeTimeSelection(timeVal, durationMinutes = 0) {
  if (!timeVal) return null
  if (typeof timeVal === 'object' && (timeVal.start || timeVal.slot)) {
    const start = toHHMM(timeVal.start || extractStartFromSlot(timeVal.slot))
    const end =
      toHHMM(timeVal.end) ||
      (start && durationMinutes > 0 ? addMinutesHHMM(start, durationMinutes) : null)
    return {
      start,
      end,
      slot: timeVal.slot || (start && end ? `${start}-${end}` : start),
    }
  }
  const raw = String(timeVal)
  const start = toHHMM(extractStartFromSlot(raw) || raw)
  if (!start) return null
  const end = durationMinutes > 0 ? addMinutesHHMM(start, durationMinutes) : null
  return { start, end, slot: raw }
}

export function toHHMM(timeStr) {
  if (!timeStr) return null
  const s = String(timeStr).trim()
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) {
    const [h, m] = s.split(':')
    return `${String(parseInt(h, 10)).padStart(2, '0')}:${m}`
  }
  const match = s.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i)
  if (!match) return null
  let hour = parseInt(match[1], 10)
  const minutes = match[2]
  const period = (match[3] || '').toUpperCase()
  if (period === 'PM' && hour < 12) hour += 12
  if (period === 'AM' && hour === 12) hour = 0
  return `${String(hour).padStart(2, '0')}:${minutes}`
}

function extractStartFromSlot(slot) {
  if (!slot) return null
  const part = String(slot).split(/\s*[-–—]\s*/)[0]?.trim()
  return part || null
}

export function addMinutesHHMM(hhmm, add) {
  const [h, m] = String(hhmm).split(':').map(Number)
  const total = (h || 0) * 60 + (m || 0) + Math.max(0, add || 0)
  const nh = Math.floor(total / 60) % 24
  const nm = total % 60
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`
}

export function timeToMinutes(hhmm) {
  const t = toHHMM(hhmm)
  if (!t) return null
  const [h, m] = t.split(':').map(Number)
  return h * 60 + m
}

export function rangesOverlap(startA, endA, startB, endB) {
  const a0 = timeToMinutes(startA)
  const a1 = timeToMinutes(endA)
  const b0 = timeToMinutes(startB)
  const b1 = timeToMinutes(endB)
  if ([a0, a1, b0, b1].some((v) => v == null)) return false
  return a0 < b1 && b0 < a1
}

/**
 * Build segment schedule for services.
 * continuous → chain from startTime
 * per_service → use serviceTimeSelections[key]
 */
export function buildServiceSegmentSchedule({
  services = [],
  schedulingMode = SCHEDULING_MODE_CONTINUOUS,
  startTime = null,
  serviceTimeSelections = {},
  appointmentDate = null,
}) {
  const list = Array.isArray(services) ? services : []
  const perService = schedulingMode === SCHEDULING_MODE_PER_SERVICE
  let cursor = null
  if (!perService) {
    const norm = normalizeTimeSelection(startTime)
    cursor = norm?.start || toHHMM(startTime)
  }

  const segments = []
  for (let index = 0; index < list.length; index++) {
    const service = list[index]
    const key = serviceScheduleKey(service, index)
    const duration = parseDurationMinutes(service)
    let start = null
    let end = null
    let date = appointmentDate || null

    if (perService) {
      const sel = serviceTimeSelections?.[key] || serviceTimeSelections?.[service.id]
      const norm = normalizeTimeSelection(sel, duration)
      start = norm?.start || null
      end = norm?.end || (start ? addMinutesHHMM(start, duration) : null)
      date = sel?.date || service.scheduled_date || appointmentDate || null
    } else {
      start = cursor
      end = start ? addMinutesHHMM(start, duration) : null
      if (end) cursor = end
    }

    segments.push({
      key,
      index,
      service,
      date,
      start_time: start,
      end_time: end,
      duration_minutes: duration,
    })
  }
  return segments
}

export function validatePerServiceSelections({
  services = [],
  serviceTimeSelections = {},
  appointmentDate = null,
  dayNumber = null,
  requireAll = true,
}) {
  const list = (Array.isArray(services) ? services : []).filter((s) => {
    if (dayNumber == null) return true
    return (Number(s.day_number) || 1) === Number(dayNumber)
  })

  if (list.length === 0) {
    return { ok: false, error: 'No services to schedule.' }
  }

  const segments = []
  for (let i = 0; i < services.length; i++) {
    const service = services[i]
    if (dayNumber != null && (Number(service.day_number) || 1) !== Number(dayNumber)) continue
    const key = serviceScheduleKey(service, i)
    const duration = parseDurationMinutes(service)
    const sel = serviceTimeSelections?.[key]
    const norm = normalizeTimeSelection(sel, duration)
    if (!norm?.start) {
      // While picking times one-by-one, only validate overlaps among already-chosen slots
      if (requireAll) {
        return {
          ok: false,
          error: `Please select a time for "${service.name || 'service'}".`,
        }
      }
      continue
    }
    const date = sel?.date || appointmentDate
    if (appointmentDate && date && String(date).slice(0, 10) !== String(appointmentDate).slice(0, 10)) {
      // Allow formatted display dates vs ISO by comparing only when both look like ISO
      const bothIso =
        /^\d{4}-\d{2}-\d{2}/.test(String(date)) &&
        /^\d{4}-\d{2}-\d{2}/.test(String(appointmentDate))
      if (bothIso) {
        return {
          ok: false,
          error: `Time for "${service.name || 'service'}" must be on the selected date.`,
        }
      }
    }
    segments.push({
      key,
      service,
      professionalId:
        service.professional?.id ||
        service.selectedProfessional?.id ||
        service.assignedProfessional?.id ||
        service.professional_id ||
        null,
      date,
      start: norm.start,
      end: norm.end || addMinutesHHMM(norm.start, duration),
    })
  }

  if (requireAll && segments.length === 0) {
    return { ok: false, error: 'Please select a time for each service.' }
  }

  for (let i = 0; i < segments.length; i++) {
    for (let j = i + 1; j < segments.length; j++) {
      const a = segments[i]
      const b = segments[j]
      if (a.date && b.date && String(a.date).slice(0, 10) !== String(b.date).slice(0, 10)) {
        const bothIso =
          /^\d{4}-\d{2}-\d{2}/.test(String(a.date)) &&
          /^\d{4}-\d{2}-\d{2}/.test(String(b.date))
        if (bothIso) continue
      }
      // Customer cannot be in two services at once (any professionals)
      if (rangesOverlap(a.start, a.end, b.start, b.end)) {
        return {
          ok: false,
          error: `Times for "${a.service.name}" and "${b.service.name}" overlap. Choose different times.`,
        }
      }
    }
  }

  return { ok: true, segments }
}

export function areAllServicesTimed(services = [], serviceTimeSelections = {}, dayNumber = null) {
  const list = Array.isArray(services) ? services : []
  for (let i = 0; i < list.length; i++) {
    const service = list[i]
    if (dayNumber != null && (Number(service.day_number) || 1) !== Number(dayNumber)) continue
    const key = serviceScheduleKey(service, i)
    const norm = normalizeTimeSelection(serviceTimeSelections?.[key], parseDurationMinutes(service))
    if (!norm?.start) return false
  }
  return list.length > 0
}

/** Earliest start among selections (for parent appointment start_time). */
export function earliestSelectionStart(serviceTimeSelections = {}) {
  let best = null
  let bestMin = Infinity
  Object.values(serviceTimeSelections || {}).forEach((sel) => {
    const norm = normalizeTimeSelection(sel)
    const mins = timeToMinutes(norm?.start)
    if (mins != null && mins < bestMin) {
      bestMin = mins
      best = norm.start
    }
  })
  return best
}

export function latestSelectionEnd(serviceTimeSelections = {}, services = []) {
  let best = null
  let bestMin = -1
  const list = Array.isArray(services) ? services : []
  list.forEach((service, index) => {
    const key = serviceScheduleKey(service, index)
    const sel = serviceTimeSelections?.[key]
    const duration = parseDurationMinutes(service)
    const norm = normalizeTimeSelection(sel, duration)
    const end = norm?.end || (norm?.start ? addMinutesHHMM(norm.start, duration) : null)
    const mins = timeToMinutes(end)
    if (mins != null && mins > bestMin) {
      bestMin = mins
      best = end
    }
  })
  return best
}

/** Stamp scheduled_* onto services for createAppointment. */
export function applyScheduleToServices(services = [], serviceTimeSelections = {}, appointmentDate = null) {
  return (Array.isArray(services) ? services : []).map((service, index) => {
    const key = serviceScheduleKey(service, index)
    const duration = parseDurationMinutes(service)
    const sel = serviceTimeSelections?.[key]
    const norm = normalizeTimeSelection(sel, duration)
    if (!norm?.start) return { ...service }
    return {
      ...service,
      scheduled_date: sel?.date || appointmentDate || service.scheduled_date || null,
      scheduled_start_time: norm.start,
      scheduled_end_time: norm.end || addMinutesHHMM(norm.start, duration),
    }
  })
}
